// SEO utility functions for dynamic Schema.org JSON-LD generation

export interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

export const getBaseUrl = (): string => {
  return import.meta.env.VITE_SITE_URL || 'https://sparkup.ch';
};

export const getLocalBusinessSchema = (): SchemaOrgData => {
  const baseUrl = getBaseUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Spark\'Up',
    description: 'Service professionnel de nettoyage de canapés, fauteuils, matelas et tissus d\'intérieur à Lausanne',
    url: baseUrl,
    telephone: '+41000000000',
    email: 'contact@sparkup.ch',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Lausanne',
      addressRegion: 'Vaud',
      addressCountry: 'CH'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '46.5197',
      longitude: '6.6323'
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: '46.5197',
        longitude: '6.6323'
      },
      geoRadius: '50000'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:00',
        closes: '19:00'
      }
    ],
    priceRange: 'CHF',
    image: `${baseUrl}/logo.png`,
    serviceType: ['Nettoyage de canapés', 'Nettoyage de fauteuils', 'Nettoyage de matelas', 'Nettoyage de tissus', 'Nettoyage de cuir']
  };
};

export const getWebPageSchema = (pageName: string, description: string): SchemaOrgData => {
  const baseUrl = getBaseUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description,
    url: baseUrl,
    inLanguage: 'fr-CH',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Spark\'Up',
      url: baseUrl
    }
  };
};

export const getServiceSchema = (serviceName: string, description: string): SchemaOrgData => {
  const baseUrl = getBaseUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Spark\'Up',
      url: baseUrl
    },
    areaServed: {
      '@type': 'City',
      name: 'Lausanne'
    },
    serviceType: 'Nettoyage professionnel'
  };
};

export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>): SchemaOrgData => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>): SchemaOrgData => {
  const baseUrl = getBaseUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${baseUrl}${item.url}`
    }))
  };
};

export const injectSchema = (schema: SchemaOrgData | SchemaOrgData[]): void => {
  const schemas = Array.isArray(schema) ? schema : [schema];
  
  schemas.forEach((schemaData) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
  });
};
